import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { Card, Skeleton, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { Email } from '../../../types/index.js';
import Loader from '../../_molecules/Loader/index.jsx';
import SaveButtons from '../../_molecules/SaveButtons/index.js';
import WarningConfirmModal from '../../_molecules/WarningConfirmModal/index.js';
import EmailFormWithChips from './EmailFormWithChips.jsx';

type Props = {
  isEmailLoading: boolean;
  handleFormSubmit?: (values: { [key: string]: any }) => void;
};

const EmailRecosPage = ({ isEmailLoading, handleFormSubmit }: Props) => {
  const { t } = useTranslation('@myn/integrations_v2');

  const reactFormContext = useFormContext();

  const values = reactFormContext.watch();

  const [isOpenModal, setOpenModal] = useState(false);
  const [isChecked, setChecked] = useState(false);
  const [isErrorNotificationShown, setIsErrorNotificationShown] =
    useState(false);
  const [isCheckboxError, setCheckboxError] = useState(false);

  const toggleCheck = useCallback(() => {
    if (!isChecked) {
      setCheckboxError(false);
      setIsErrorNotificationShown(false);
    }
    setChecked((prev) => !prev);
  }, [isChecked]);

  const handleSetValues = (value: string) => {
    const newValue = [
      ...values.emails,
      {
        id: new Date().valueOf(),
        email: value.trim(),
      },
    ];

    reactFormContext.setValue('emails', newValue, { shouldDirty: true });
  };

  const handleDelete = useCallback(
    (email: Email) => {
      const newEmails = values.emails.filter((em: Email) => em.id !== email.id);
      reactFormContext.setValue('emails', newEmails, { shouldDirty: true });
    },
    [values],
  );

  const handleOnCancelClick = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleOnSaveClick = useCallback(() => {
    if (!isChecked) {
      setIsErrorNotificationShown(true);
      setCheckboxError(true);
      return;
    }
    handleFormSubmit?.(values);
  }, [isChecked, values]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (isErrorNotificationShown && isCheckboxError) {
      enqueueSnackbar({
        title: t(
          'You cannot save without confirming you have permission to e-mail these people.',
        ),
        variant: 'error',
        removable: true,
        onDismiss: () => {
          setIsErrorNotificationShown(false);
        },
      });
    }
  }, [isErrorNotificationShown, isCheckboxError, t]);

  return isEmailLoading ? (
    <Skeleton variant="rounded" className="mt-2 h-70 w-full max-w-[910px]" />
  ) : (
    <form
      data-testid="userspage-form"
      onSubmit={handleSubmit}
      className="relative mt-3 flex w-full flex-col gap-4"
    >
      <div className="flex flex-col items-start gap-4">
        <Card className={'w-[910px] p-2'}>
          <h4 className={cn('mb-5', typographyVariants({ variant: 'h4' }))}>
            {t('Email Suggestions')}
          </h4>
          <p className={typographyVariants({ variant: 'bodyLong01' })}>
            {t(
              'Suggestion notifications will be sent to the e-mail addresses you will type below',
            )}
          </p>
          {isEmailLoading ? (
            <div
              className={
                'flex h-[calc(100vh-410px)] w-full items-center justify-center'
              }
            >
              <Loader />
            </div>
          ) : (
            <EmailFormWithChips
              isCheckboxError={isCheckboxError}
              isChecked={isChecked}
              onAdd={handleSetValues}
              onDelete={handleDelete}
              emails={values.emails}
              toggleCheck={toggleCheck}
            />
          )}
        </Card>

        <SaveButtons
          label={t('Save')}
          isUserReadOnly={false}
          onCancelClick={handleOnCancelClick}
          onActionClick={handleOnSaveClick}
          handleFormSubmit={handleFormSubmit}
        />
      </div>

      <WarningConfirmModal
        title={t('Are you sure you want to cancel?')}
        text={t('Changes could not be restored.')}
        open={isOpenModal}
        onClose={() => setOpenModal(false)}
        cancelButtonText={t('No')}
        actionButtonText={t('Yes')}
        onActionClick={() => {
          reactFormContext.reset();
          setOpenModal(false);
        }}
      />
    </form>
  );
};

export default EmailRecosPage;
